import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertTriangle,
  BadgeCheck,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Coffee,
  Download,
  Edit3,
  MapPin,
  MoreVertical,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Search,
  Settings2,
  TimerReset,
  Trash2,
  WalletCards,
  X,
} from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { formatMoney } from '../i18n/orgFormat';
import { cn } from '../utils/cn';

type TimesheetStatus = 'OPEN' | 'ON_BREAK' | 'SUBMITTED' | 'APPROVED' | 'LATE' | 'ABSENT' | 'HOLIDAY' | 'OFF' | 'CLOSED';
type ViewMode = 'day' | 'week' | 'month';
type CompensationType = 'none' | 'hourly' | 'weekly' | 'monthly' | 'yearly';
type OvertimeType = 'multiplier' | 'hourly';

type StaffOperationsData = {
  timesheets: Array<{
    id: string;
    staffId?: string | null;
    providerId?: string | null;
    locationId?: string | null;
    clockInAt: string;
    clockOutAt?: string | null;
    status: string;
    notes?: string | null;
  }>;
  rota: Array<{
    id: string;
    staffId?: string | null;
    providerId?: string | null;
    locationId?: string | null;
    startsAt: string;
    endsAt: string;
    role?: string | null;
    status: string;
  }>;
  payrollExports: Array<{ id: string; rangeStart: string; rangeEnd: string; status: string; exportFormat: string }>;
};

type UserOption = {
  id: string;
  firstName: string;
  lastName: string;
  title?: string | null;
  timesheetEnabled?: boolean;
  linkedProfileType?: 'PROVIDER' | 'STAFF' | null;
  linkedProviderProfileId?: string | null;
  linkedStaffProfileId?: string | null;
};

type BreakEntry = {
  id: string;
  type: string;
  minutes: number;
  paid: boolean;
  startedAt?: string | null;
  endedAt?: string | null;
};

type LocalEntry = {
  id: string;
  staffId: string;
  staffName: string;
  location: string;
  dayLabel: string;
  dateIso: string;
  clockInAt?: string | null;
  breakMinutes: number;
  paidBreakMinutes: number;
  clockOutAt?: string | null;
  status: TimesheetStatus;
  scheduledStart: string;
  scheduledEnd: string;
  notes?: string;
  breaks: BreakEntry[];
  locationCaptured?: string;
};

type TimesheetForm = {
  staffId: string;
  location: string;
  date: string;
  clockIn: string;
  clockOut: string;
  notes: string;
  breaks: BreakEntry[];
};

const locations = ['Elen1 Zacatecas', 'Remote', 'Front desk', 'Clinic floor'];
const breakTypes = [
  { type: 'Lunch', minutes: 30, paid: false },
  { type: 'Meeting', minutes: 15, paid: true },
  { type: 'Training', minutes: 45, paid: true },
  { type: 'Personal', minutes: 10, paid: false },
];

const statusStyles: Record<TimesheetStatus, string> = {
  OPEN: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  ON_BREAK: 'border-violet-200 bg-violet-50 text-violet-700',
  SUBMITTED: 'border-sky-200 bg-sky-50 text-sky-700',
  APPROVED: 'border-lime-200 bg-lime-50 text-lime-700',
  LATE: 'border-orange-200 bg-orange-50 text-orange-700',
  ABSENT: 'border-rose-200 bg-rose-50 text-rose-700',
  HOLIDAY: 'border-yellow-200 bg-yellow-50 text-yellow-700',
  OFF: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  CLOSED: 'border-slate-200 bg-slate-100 text-slate-600',
};

function formatTime(value?: string | null) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDuration(hours: number) {
  const totalMinutes = Math.max(0, Math.round(hours * 60));
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${wholeHours}h ${String(minutes).padStart(2, '0')}m`;
}

function hoursBetween(start?: string | null, end?: string | null, unpaidBreakMinutes = 0) {
  if (!start) return 0;
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return 0;
  return Math.max(0, (endMs - startMs) / 36e5 - unpaidBreakMinutes / 60);
}

function grossHours(start?: string | null, end?: string | null) {
  if (!start || !end) return 0;
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return 0;
  return (endMs - startMs) / 36e5;
}

function toCurrency(value: number) {
  return formatMoney(value);
}

function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function startOfWeek(date: Date) {
  const next = new Date(date);
  const day = next.getDay();
  next.setDate(next.getDate() + (day === 0 ? -6 : 1 - day));
  next.setHours(0, 0, 0, 0);
  return next;
}

function isoAt(base: Date, dayOffset: number, hour: number, minute = 0) {
  const value = new Date(base);
  value.setDate(value.getDate() + dayOffset);
  value.setHours(hour, minute, 0, 0);
  return value.toISOString();
}

function toInputDate(value: Date) {
  return value.toISOString().slice(0, 10);
}

function toInputTime(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function combineDateTime(date: string, time: string) {
  if (!date || !time) return null;
  const value = new Date(`${date}T${time}:00`);
  return Number.isNaN(value.getTime()) ? null : value.toISOString();
}

function statusLabel(status: TimesheetStatus) {
  return status.replace('_', ' ');
}

function buildBreak(type: string, minutes: number, paid: boolean): BreakEntry {
  return { id: `break-${Date.now()}-${Math.random().toString(16).slice(2)}`, type, minutes, paid };
}

function buildDemoEntries(users: UserOption[], weekStart: Date): LocalEntry[] {
  const names = users.length
    ? users.map((user) => ({ id: user.id, name: `${user.firstName} ${user.lastName}`.trim() || user.id }))
    : [
        { id: 'demo-1', name: 'William Anderson' },
        { id: 'demo-2', name: 'Kate Pipson' },
        { id: 'demo-3', name: 'Marta Colombo' },
      ];
  return names.flatMap((staff, staffIndex) =>
    [0, 1, 2, 3, 4].map((dayOffset) => {
      const status: TimesheetStatus =
        dayOffset === 3 && staffIndex === 1 ? 'HOLIDAY' : dayOffset === 4 && staffIndex === 2 ? 'ABSENT' : dayOffset === 2 ? 'OPEN' : 'APPROVED';
      const clockInAt = status === 'ABSENT' || status === 'HOLIDAY' ? null : isoAt(weekStart, dayOffset, 8, staffIndex === 0 ? 30 : 42);
      const clockOutAt = status === 'OPEN' ? null : status === 'ABSENT' || status === 'HOLIDAY' ? null : isoAt(weekStart, dayOffset, 17, 30);
      const breaks =
        status === 'ABSENT' || status === 'HOLIDAY'
          ? []
          : [
              {
                id: `${staff.id}-${dayOffset}-lunch`,
                type: 'Lunch',
                minutes: 30,
                paid: false,
                startedAt: isoAt(weekStart, dayOffset, 12, 0),
                endedAt: isoAt(weekStart, dayOffset, 12, 30),
              },
              {
                id: `${staff.id}-${dayOffset}-meeting`,
                type: 'Meeting',
                minutes: 15,
                paid: true,
                startedAt: isoAt(weekStart, dayOffset, 15, 0),
                endedAt: isoAt(weekStart, dayOffset, 15, 15),
              },
            ];
      return {
        id: `${staff.id}-${dayOffset}`,
        staffId: staff.id,
        staffName: staff.name,
        location: locations[(staffIndex + dayOffset) % locations.length],
        dayLabel: new Date(isoAt(weekStart, dayOffset, 9)).toLocaleDateString([], { weekday: 'short', day: 'numeric' }),
        dateIso: isoAt(weekStart, dayOffset, 0),
        clockInAt,
        clockOutAt,
        breakMinutes: breaks.reduce((total, entry) => total + entry.minutes, 0),
        paidBreakMinutes: breaks.filter((entry) => entry.paid).reduce((total, entry) => total + entry.minutes, 0),
        status,
        scheduledStart: '9:30 AM',
        scheduledEnd: '5:30 PM',
        notes: status === 'ABSENT' ? 'Manager review required' : status === 'OPEN' ? 'Ongoing shift updates in real time' : undefined,
        breaks,
        locationCaptured: locations[(staffIndex + dayOffset) % locations.length],
      };
    }),
  );
}

export default function TimesheetsPage() {
  const { fetchJson } = useApiClient();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [data, setData] = useState<StaffOperationsData | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [entries, setEntries] = useState<LocalEntry[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Timesheet created successfully.');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [deleteEntryId, setDeleteEntryId] = useState<string | null>(null);
  const [confirmClockOutId, setConfirmClockOutId] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [compensationType, setCompensationType] = useState<CompensationType>('hourly');
  const [hourlyRate, setHourlyRate] = useState('24');
  const [regularHours, setRegularHours] = useState('40');
  const [regularHoursType, setRegularHoursType] = useState<'day' | 'week'>('week');
  const [overtimeEnabled, setOvertimeEnabled] = useState(true);
  const [overtimeType, setOvertimeType] = useState<OvertimeType>('multiplier');
  const [overtimeValue, setOvertimeValue] = useState('1.5');
  const [autoClockIn, setAutoClockIn] = useState(false);
  const [autoClockOut, setAutoClockOut] = useState(true);
  const [autoBreaks, setAutoBreaks] = useState(true);
  const [form, setForm] = useState<TimesheetForm>(() => ({
    staffId: 'demo-1',
    location: locations[0],
    date: toInputDate(new Date()),
    clockIn: '09:30',
    clockOut: '17:30',
    notes: '',
    breaks: [buildBreak('Lunch', 30, false)],
  }));

  async function load() {
    setError('');
    try {
      const [operations, loadedUsers] = await Promise.all([
        fetchJson<StaffOperationsData>('/staff-operations'),
        fetchJson<UserOption[]>('/users').catch(() => []),
      ]);
      const timesheetUsers = loadedUsers.filter((user) => user.timesheetEnabled || user.linkedProfileType === 'STAFF' || user.linkedProfileType === 'PROVIDER');
      setData(operations);
      setUsers(timesheetUsers);
      setEntries((current) => (current.length ? current : buildDemoEntries(timesheetUsers, weekStart)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load timesheets.');
      setEntries((current) => (current.length ? current : buildDemoEntries([], weekStart)));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    setEntries(buildDemoEntries(users, weekStart));
    setSelectedEntryId(null);
  }, [weekStart]);

  const staffOptions = useMemo(() => {
    const map = new Map<string, string>();
    entries.forEach((entry) => map.set(entry.staffId, entry.staffName));
    users.forEach((user) => map.set(user.id, `${user.firstName} ${user.lastName}`.trim() || user.id));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [entries, users]);

  const selectedEntry = entries.find((entry) => entry.id === selectedEntryId) ?? null;
  const deleteEntry = entries.find((entry) => entry.id === deleteEntryId) ?? null;
  const confirmClockOutEntry = entries.find((entry) => entry.id === confirmClockOutId) ?? null;

  const filteredEntries = useMemo(() => {
    const rows = entries.filter((entry) => {
      if (selectedStaffId !== 'all' && entry.staffId !== selectedStaffId) return false;
      if (locationFilter !== 'all' && entry.location !== locationFilter) return false;
      return true;
    });
    return [...rows].sort((a, b) => {
      const aTime = new Date(a.dateIso).getTime();
      const bTime = new Date(b.dateIso).getTime();
      return sortNewestFirst ? bTime - aTime : aTime - bTime;
    });
  }, [entries, locationFilter, selectedStaffId, sortNewestFirst]);

  const activeEntry = entries.find((entry) => entry.status === 'OPEN' || entry.status === 'ON_BREAK');
  const paidHours = filteredEntries.reduce((total, entry) => total + hoursBetween(entry.clockInAt, entry.clockOutAt, entry.breakMinutes - entry.paidBreakMinutes), 0);
  const totalGrossHours = filteredEntries.reduce((total, entry) => total + grossHours(entry.clockInAt, entry.clockOutAt), 0);
  const paidBreakHours = filteredEntries.reduce((total, entry) => total + entry.paidBreakMinutes / 60, 0);
  const regularLimit = Number(regularHours || 0);
  const overtimeHours = overtimeEnabled ? Math.max(0, paidHours - regularLimit) : 0;
  const regularPaidHours = Math.max(0, paidHours - overtimeHours);
  const overtimeRate = overtimeType === 'hourly' ? Number(overtimeValue || 0) : Number(hourlyRate || 0) * Number(overtimeValue || 1);
  const grossPay = compensationType === 'none' ? 0 : regularPaidHours * Number(hourlyRate || 0) + overtimeHours * overtimeRate;
  const approvalCount = filteredEntries.filter((entry) => ['SUBMITTED', 'LATE', 'ABSENT'].includes(entry.status)).length;
  const statusCounts = {
    present: filteredEntries.filter((entry) => ['OPEN', 'ON_BREAK', 'SUBMITTED', 'APPROVED'].includes(entry.status)).length,
    waiting: filteredEntries.filter((entry) => entry.status === 'SUBMITTED').length,
    off: filteredEntries.filter((entry) => entry.status === 'OFF').length,
    closed: filteredEntries.filter((entry) => entry.status === 'CLOSED').length,
    absent: filteredEntries.filter((entry) => entry.status === 'ABSENT').length,
    noShift: filteredEntries.filter((entry) => ['HOLIDAY', 'OFF'].includes(entry.status)).length,
  };

  function resetForm(staffId = staffOptions[0]?.id || 'demo-1') {
    setForm({
      staffId,
      location: locationFilter === 'all' ? locations[0] : locationFilter,
      date: toInputDate(new Date()),
      clockIn: '09:30',
      clockOut: '17:30',
      notes: '',
      breaks: [buildBreak('Lunch', 30, false)],
    });
  }

  function openCreateForm() {
    setEditingEntryId(null);
    resetForm();
    setShowForm(true);
  }

  function openEditForm(entry: LocalEntry) {
    setEditingEntryId(entry.id);
    setForm({
      staffId: entry.staffId,
      location: entry.location,
      date: toInputDate(new Date(entry.dateIso)),
      clockIn: toInputTime(entry.clockInAt),
      clockOut: toInputTime(entry.clockOutAt),
      notes: entry.notes || '',
      breaks: entry.breaks.length ? entry.breaks : [buildBreak('Lunch', 30, false)],
    });
    setShowForm(true);
  }

  async function persistTimesheet(clockInAt: string, clockOutAt?: string | null) {
    await fetchJson('/staff-operations/timesheets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clockInAt, clockOutAt: clockOutAt || undefined, notes: form.notes || 'Created from Timesheets module' }),
    });
  }

  async function createTimesheet(event?: FormEvent) {
    event?.preventDefault();
    const clockInAt = combineDateTime(form.date, form.clockIn);
    const clockOutAt = combineDateTime(form.date, form.clockOut);
    if (!clockInAt) {
      setError('Clock-in time is required.');
      return;
    }
    if (clockOutAt && new Date(clockOutAt) <= new Date(clockInAt)) {
      setError('Clock-out must be after clock-in.');
      return;
    }
    const staff = staffOptions.find((item) => item.id === form.staffId) || staffOptions[0] || { id: 'demo-1', name: 'William Anderson' };
    const breakMinutes = form.breaks.reduce((total, entry) => total + Number(entry.minutes || 0), 0);
    const paidBreakMinutes = form.breaks.filter((entry) => entry.paid).reduce((total, entry) => total + Number(entry.minutes || 0), 0);
    setCreating(true);
    setError('');
    try {
      if (!editingEntryId) {
        await persistTimesheet(clockInAt, clockOutAt);
      }
      const nextEntry: LocalEntry = {
        id: editingEntryId || `local-${Date.now()}`,
        staffId: staff.id,
        staffName: staff.name,
        location: form.location,
        dayLabel: new Date(`${form.date}T09:00:00`).toLocaleDateString([], { weekday: 'short', day: 'numeric' }),
        dateIso: new Date(`${form.date}T00:00:00`).toISOString(),
        clockInAt,
        clockOutAt,
        breakMinutes,
        paidBreakMinutes,
        status: clockOutAt ? 'SUBMITTED' : 'OPEN',
        scheduledStart: '9:30 AM',
        scheduledEnd: '5:30 PM',
        notes: form.notes,
        breaks: form.breaks.map((entry) => ({ ...entry, minutes: Number(entry.minutes || 0) })),
        locationCaptured: form.location,
      };
      setEntries((current) => (editingEntryId ? current.map((entry) => (entry.id === editingEntryId ? nextEntry : entry)) : [nextEntry, ...current]));
      setSelectedEntryId(nextEntry.id);
      setSuccessMessage(editingEntryId ? 'Timesheet updated successfully.' : 'Timesheet created successfully.');
      setShowSuccess(true);
      setShowForm(false);
      setEditingEntryId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save timesheet.');
    } finally {
      setCreating(false);
    }
  }

  async function quickClockIn() {
    const staff = staffOptions[0] || { id: 'demo-1', name: 'William Anderson' };
    const now = new Date();
    setCreating(true);
    setError('');
    try {
      await fetchJson('/staff-operations/timesheets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clockInAt: now.toISOString(), notes: 'Clocked in from Timesheets module' }),
      });
      const nextEntry: LocalEntry = {
        id: `local-${Date.now()}`,
        staffId: staff.id,
        staffName: staff.name,
        location: locationFilter === 'all' ? locations[0] : locationFilter,
        dayLabel: now.toLocaleDateString([], { weekday: 'short', day: 'numeric' }),
        dateIso: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString(),
        clockInAt: now.toISOString(),
        clockOutAt: null,
        breakMinutes: 0,
        paidBreakMinutes: 0,
        status: 'OPEN',
        scheduledStart: '9:30 AM',
        scheduledEnd: '5:30 PM',
        notes: 'Live clock-in',
        breaks: [],
        locationCaptured: locationFilter === 'all' ? locations[0] : locationFilter,
      };
      setEntries((current) => [nextEntry, ...current]);
      setSelectedEntryId(nextEntry.id);
      setSuccessMessage('Clocked in successfully.');
      setShowSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to clock in.');
    } finally {
      setCreating(false);
    }
  }

  function updateEntry(id: string, partial: Partial<LocalEntry>) {
    setEntries((current) => current.map((entry) => (entry.id === id ? { ...entry, ...partial } : entry)));
  }

  function startBreak(entry: LocalEntry, breakType = breakTypes[0]) {
    const startedAt = new Date().toISOString();
    const newBreak = { ...buildBreak(breakType.type, breakType.minutes, breakType.paid), startedAt };
    updateEntry(entry.id, {
      status: 'ON_BREAK',
      breaks: [...entry.breaks, newBreak],
      breakMinutes: entry.breakMinutes + breakType.minutes,
      paidBreakMinutes: entry.paidBreakMinutes + (breakType.paid ? breakType.minutes : 0),
    });
  }

  function endBreak(entry: LocalEntry) {
    updateEntry(entry.id, {
      status: 'OPEN',
      breaks: entry.breaks.map((item, index) => (index === entry.breaks.length - 1 ? { ...item, endedAt: new Date().toISOString() } : item)),
    });
  }

  function clockOut(entry: LocalEntry) {
    const now = new Date();
    const scheduledEnd = new Date(entry.dateIso);
    const [hour, minute, period] = entry.scheduledEnd.replace(':', ' ').split(' ');
    const parsedHour = Number(hour);
    scheduledEnd.setHours(period === 'PM' && parsedHour < 12 ? parsedHour + 12 : parsedHour, Number(minute || 0), 0, 0);
    if (now < scheduledEnd && entry.status !== 'ON_BREAK') {
      setConfirmClockOutId(entry.id);
      return;
    }
    updateEntry(entry.id, { status: 'SUBMITTED', clockOutAt: now.toISOString() });
  }

  function confirmClockOut() {
    if (!confirmClockOutEntry) return;
    updateEntry(confirmClockOutEntry.id, { status: 'SUBMITTED', clockOutAt: new Date().toISOString() });
    setConfirmClockOutId(null);
  }

  function deleteTimesheet() {
    if (!deleteEntryId) return;
    setEntries((current) => current.filter((entry) => entry.id !== deleteEntryId));
    if (selectedEntryId === deleteEntryId) setSelectedEntryId(null);
    setDeleteEntryId(null);
  }

  function exportPayroll() {
    const header = ['Team member', 'Location', 'Day', 'Status', 'Clock in', 'Clock out', 'Break minutes', 'Paid break minutes', 'Paid hours', 'Gross pay'];
    const rows = filteredEntries.map((entry) => {
      const paid = hoursBetween(entry.clockInAt, entry.clockOutAt, entry.breakMinutes - entry.paidBreakMinutes);
      const overtime = overtimeEnabled ? Math.max(0, paid - regularLimit) : 0;
      const regular = Math.max(0, paid - overtime);
      const pay = compensationType === 'none' ? 0 : regular * Number(hourlyRate || 0) + overtime * overtimeRate;
      return [
        entry.staffName,
        entry.location,
        entry.dayLabel,
        entry.status,
        formatTime(entry.clockInAt),
        formatTime(entry.clockOutAt),
        String(entry.breakMinutes),
        String(entry.paidBreakMinutes),
        paid.toFixed(2),
        pay.toFixed(2),
      ];
    });
    const csv = [header, ...rows].map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `elen1-timesheets-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  const rangeLabel =
    viewMode === 'day'
      ? weekStart.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
      : viewMode === 'month'
        ? weekStart.toLocaleDateString([], { month: 'long', year: 'numeric' })
        : 'This Week';

  return (
    <div className="min-h-full bg-slate-50 p-6">
      <div className="mx-auto w-full max-w-[1680px] space-y-5">
        {showSuccess ? (
          <div className="flex items-center justify-between rounded-lg border border-lime-300 bg-lime-50 px-5 py-4 text-lime-900">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="text-lime-600" size={22} />
              <span className="text-lg font-semibold">{successMessage}</span>
            </div>
            <button type="button" onClick={() => setShowSuccess(false)} className="rounded-lg p-1 text-lime-900/70 hover:bg-lime-100 hover:text-lime-950" aria-label="Dismiss success">
              <X size={18} />
            </button>
          </div>
        ) : null}

        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Timesheets</p>
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-700">Beta</span>
            </div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-950">Clock-ins, breaks, approvals, overtime, and payroll wages</h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">
              Manage live shifts, missed punches, break allowances, admin corrections, wage setup, and payroll-ready hours from one operational view.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setShowConfig(true)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Settings2 size={16} /> Configure
            </button>
            <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <RotateCcw size={16} /> Refresh
            </button>
            <button type="button" onClick={exportPayroll} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Download size={16} /> Payroll CSV
            </button>
            <button type="button" disabled={creating} onClick={openCreateForm} className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 disabled:opacity-60">
              <Plus size={16} /> Add Timesheet
            </button>
          </div>
        </header>

        {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

        <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px] 2xl:grid-cols-[minmax(0,1fr)_400px]">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <FilterSelect icon={<MapPin size={14} />} value={locationFilter} onChange={setLocationFilter}>
                  <option value="all">All locations</option>
                  {locations.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </FilterSelect>
                <FilterSelect icon={<Search size={14} />} value={selectedStaffId} onChange={setSelectedStaffId}>
                  <option value="all">Select a team member</option>
                  {staffOptions.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.name}
                    </option>
                  ))}
                </FilterSelect>
                <div className="inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <button type="button" onClick={() => setWeekStart((current) => new Date(current.getTime() - 7 * 86400000))} className="px-3 py-2 text-slate-500 hover:bg-slate-50" aria-label="Previous range">
                    <ChevronLeft size={16} />
                  </button>
                  <span className="min-w-36 border-x border-slate-200 px-5 py-2 text-center text-sm font-semibold text-slate-700">{rangeLabel}</span>
                  <button type="button" onClick={() => setWeekStart((current) => new Date(current.getTime() + 7 * 86400000))} className="px-3 py-2 text-slate-500 hover:bg-slate-50" aria-label="Next range">
                    <ChevronRight size={16} />
                  </button>
                </div>
                <select value={viewMode} onChange={(event) => setViewMode(event.target.value as ViewMode)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700">
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </div>
              <div className="flex gap-2">
                <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
                  <input type="radio" checked={sortNewestFirst} onChange={() => setSortNewestFirst(true)} />
                  Newest first
                </label>
                <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
                  <input type="radio" checked={!sortNewestFirst} onChange={() => setSortNewestFirst(false)} />
                  Oldest first
                </label>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <Metric label="Off" value={`${statusCounts.off} day`} color="bg-indigo-400" />
              <Metric label="Absent" value={`${statusCounts.absent} day`} color="bg-rose-400" />
              <Metric label="Closed" value={`${statusCounts.closed} day`} color="bg-sky-400" />
              <Metric label="Present" value={`${statusCounts.present} day`} color="bg-emerald-400" />
              <Metric label="Waiting" value={`${statusCounts.waiting} day`} color="bg-amber-400" />
              <Metric label="No shifts" value={`${statusCounts.noShift} day`} color="bg-slate-400" />
            </div>
          </div>

          <section className={cn('rounded-lg border p-5 shadow-sm', activeEntry?.status === 'ON_BREAK' ? 'border-violet-200 bg-violet-50' : 'border-sky-200 bg-sky-50')}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Avatar clock</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-950">{activeEntry ? activeEntry.staffName : 'Shift starting soon'}</h2>
                <p className="mt-1 text-sm text-slate-600">
                  {activeEntry
                    ? activeEntry.status === 'ON_BREAK'
                      ? `On ${activeEntry.breaks.at(-1)?.type || 'Break'} - ${activeEntry.breaks.at(-1)?.minutes || 0}m scheduled`
                      : `On shift - ending at ${activeEntry.scheduledEnd}`
                    : 'Scheduled to work from 9:30AM - 5:30PM'}
                </p>
              </div>
              <Clock3 className={activeEntry?.status === 'ON_BREAK' ? 'text-violet-700' : 'text-sky-700'} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {!activeEntry ? (
                <button
                  className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
                  disabled={creating}
                  onClick={() => void quickClockIn()}
                >
                  <Play size={15} /> Clock In
                </button>
              ) : activeEntry.status === 'ON_BREAK' ? (
                <button type="button" onClick={() => endBreak(activeEntry)} className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-white px-4 py-2 text-sm font-semibold text-violet-700">
                  <Play size={15} /> End Break
                </button>
              ) : (
                <button type="button" onClick={() => startBreak(activeEntry)} className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700">
                  <Coffee size={15} /> Start Break
                </button>
              )}
              {activeEntry ? (
                <button type="button" onClick={() => clockOut(activeEntry)} className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-white px-4 py-2 text-sm font-semibold text-sky-700">
                  <TimerReset size={15} /> Clock Out
                </button>
              ) : null}
            </div>
          </section>
        </section>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_400px] 2xl:grid-cols-[minmax(0,1fr)_430px]">
          <main className="space-y-5">
            <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="grid min-w-[920px] grid-cols-[150px_130px_minmax(300px,1fr)_140px_140px_170px] border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <span>Team member</span>
                <span>Check-in</span>
                <span>Shift activity</span>
                <span>Check-out</span>
                <span>Total hours</span>
                <span className="text-right">Actions</span>
              </div>
              <div className="divide-y divide-slate-100 overflow-x-auto">
                {filteredEntries.map((entry) => {
                  const unpaidBreakMinutes = entry.breakMinutes - entry.paidBreakMinutes;
                  const paid = hoursBetween(entry.clockInAt, entry.clockOutAt, unpaidBreakMinutes);
                  return (
                    <div
                      key={entry.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedEntryId(entry.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedEntryId(entry.id);
                        }
                      }}
                      className={cn(
                        'grid min-w-[920px] cursor-pointer grid-cols-[150px_130px_minmax(300px,1fr)_140px_140px_170px] items-center gap-4 px-4 py-3 text-left text-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-200',
                        selectedEntryId === entry.id && 'bg-sky-50/70',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                          {initials(entry.staffName)}
                          <Clock3 className="absolute -bottom-1 -right-1 rounded-full border border-white bg-sky-100 p-0.5 text-sky-700" size={17} />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{entry.staffName}</p>
                          <p className="text-xs text-slate-500">{entry.dayLabel}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700">{formatTime(entry.clockInAt)}</p>
                        <p className="text-xs text-slate-500">{entry.scheduledStart}</p>
                      </div>
                      <div>
                        <div className="relative h-10 rounded-full bg-slate-100">
                          <div className="absolute left-3 right-3 top-1/2 h-0.5 -translate-y-1/2 bg-emerald-300" />
                          <TimelineDot left="8%" active={Boolean(entry.clockInAt)} label={`Clock-in ${formatTime(entry.clockInAt)}`} />
                          <TimelineDot left="36%" active={entry.breaks.length > 0} label={`${entry.breaks.length} break event(s)`} />
                          <TimelineDot left="62%" active={entry.status === 'ON_BREAK'} label="Break active" tone="violet" />
                          <TimelineDot left="82%" active={Boolean(entry.clockOutAt)} label={`Clock-out ${formatTime(entry.clockOutAt)}`} />
                          <span className={cn('absolute right-3 top-1/2 -translate-y-1/2 rounded-full border px-2 py-1 text-xs font-semibold', statusStyles[entry.status])}>{statusLabel(entry.status)}</span>
                        </div>
                        <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                          <MapPin size={12} /> {entry.location} · {entry.breakMinutes}m breaks · {entry.paidBreakMinutes}m paid
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700">{formatTime(entry.clockOutAt)}</p>
                        <p className="text-xs text-slate-500">{entry.scheduledEnd}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{formatDuration(paid)}</p>
                        <p className="text-xs text-slate-500">{formatDuration(grossHours(entry.clockInAt, entry.clockOutAt))} worked</p>
                      </div>
                      <div className="flex justify-end gap-2" onClick={(event) => event.stopPropagation()}>
                        <IconAction label="Start break" icon={<Pause size={14} />} onClick={() => startBreak(entry)} disabled={!['OPEN', 'SUBMITTED', 'APPROVED'].includes(entry.status)} />
                        <IconAction label="Clock out" icon={<TimerReset size={14} />} onClick={() => clockOut(entry)} disabled={!['OPEN', 'ON_BREAK'].includes(entry.status)} />
                        <IconAction label="Approve" icon={<Check size={14} />} onClick={() => updateEntry(entry.id, { status: 'APPROVED' })} disabled={entry.status === 'APPROVED'} />
                        <IconAction label="More" icon={<MoreVertical size={14} />} onClick={() => setSelectedEntryId(entry.id)} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="grid gap-4 md:grid-cols-3">
              <SummaryPanel icon={<WalletCards size={18} />} label="Total hours paid" value={formatDuration(paidHours)} detail={`${formatDuration(paidBreakHours)} paid breaks`} />
              <SummaryPanel icon={<CalendarClock size={18} />} label="Worked time" value={formatDuration(totalGrossHours)} detail={`${approvalCount} needs review`} />
              <SummaryPanel icon={<BadgeCheck size={18} />} label="Estimated wages" value={toCurrency(grossPay)} detail={`${formatDuration(overtimeHours)} overtime · ${data?.payrollExports?.length ?? 0} exports`} />
            </section>
          </main>

          <aside className="space-y-5">
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-slate-950">Timesheets & Wages</h2>
                  <p className="text-xs text-slate-500">Set compensation, regular hours, and overtime rules for wage reporting.</p>
                </div>
                <Settings2 size={18} className="text-slate-500" />
              </div>
              <div className="mt-5 space-y-4">
                <label className="block text-sm">
                  <span className="text-slate-500">Compensation type</span>
                  <select value={compensationType} onChange={(event) => setCompensationType(event.target.value as CompensationType)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2">
                    <option value="none">None</option>
                    <option value="hourly">Hourly pay</option>
                    <option value="weekly">Weekly pay</option>
                    <option value="monthly">Monthly pay</option>
                    <option value="yearly">Yearly pay</option>
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-slate-500">Rate</span>
                  <input value={hourlyRate} onChange={(event) => setHourlyRate(event.target.value)} type="number" min="0" step="0.01" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <span className="text-slate-500">Regular hours</span>
                    <input value={regularHours} onChange={(event) => setRegularHours(event.target.value)} type="number" min="1" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
                  </label>
                  <label className="block text-sm">
                    <span className="text-slate-500">Type</span>
                    <select value={regularHoursType} onChange={(event) => setRegularHoursType(event.target.value as 'day' | 'week')} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2">
                      <option value="week">Per week</option>
                      <option value="day">Per day</option>
                    </select>
                  </label>
                </div>
                <label className="flex items-start gap-3 rounded-lg bg-sky-50 px-3 py-3 text-sm">
                  <input type="checkbox" className="mt-1" checked={overtimeEnabled} onChange={(event) => setOvertimeEnabled(event.target.checked)} />
                  <span>
                    <span className="block font-semibold text-slate-900">Overtime pay</span>
                    <span className="text-xs text-slate-500">Calculate additional earnings above regular working hours.</span>
                  </span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <span className="text-slate-500">Overtime type</span>
                    <select value={overtimeType} onChange={(event) => setOvertimeType(event.target.value as OvertimeType)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2">
                      <option value="multiplier">Multiplier</option>
                      <option value="hourly">Hourly rate</option>
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="text-slate-500">{overtimeType === 'hourly' ? 'Hourly rate' : 'Multiplier'}</span>
                    <input value={overtimeValue} onChange={(event) => setOvertimeValue(event.target.value)} type="number" min="0" step="0.1" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
                  </label>
                </div>
              </div>
            </section>

            <ActivitySidebar
              entry={selectedEntry}
              onEdit={openEditForm}
              onDelete={(entry) => setDeleteEntryId(entry.id)}
              onApprove={(entry) => updateEntry(entry.id, { status: 'APPROVED' })}
              onStartBreak={startBreak}
              onEndBreak={endBreak}
            />

            <section className="rounded-lg border border-amber-200 bg-amber-50 p-5">
              <div className="flex gap-3">
                <AlertTriangle className="mt-0.5 text-amber-600" size={18} />
                <div>
                  <h2 className="font-semibold text-amber-950">Manager approvals</h2>
                  <p className="mt-1 text-sm text-amber-800">{approvalCount} entries need review before payroll. Admins can edit ongoing and completed timesheets from the activity panel.</p>
                </div>
              </div>
            </section>
          </aside>
        </div>
      </div>

      {showForm ? (
        <TimesheetModal
          form={form}
          setForm={setForm}
          staffOptions={staffOptions}
          editing={Boolean(editingEntryId)}
          creating={creating}
          onSubmit={createTimesheet}
          onClose={() => {
            setShowForm(false);
            setEditingEntryId(null);
          }}
        />
      ) : null}

      {showConfig ? (
        <ConfirmPanel
          title="Timesheet settings"
          description="Automate scheduled clock-ins, clock-outs, and scheduled breaks for staff with timesheets enabled."
          confirmLabel="Save Settings"
          tone="sky"
          onCancel={() => setShowConfig(false)}
          onConfirm={() => setShowConfig(false)}
        >
          <div className="space-y-3">
            <ToggleRow checked={autoClockIn} onChange={setAutoClockIn} title="Auto Clock In" description="Clock in staff automatically at the beginning of scheduled shifts." />
            <ToggleRow checked={autoClockOut} onChange={setAutoClockOut} title="Auto Clock Out" description="Clock out staff automatically when their scheduled shift ends." />
            <ToggleRow checked={autoBreaks} onChange={setAutoBreaks} title="Auto Start and End Scheduled Breaks" description="Use calendar blockouts to start and stop scheduled breaks." />
          </div>
        </ConfirmPanel>
      ) : null}

      {confirmClockOutEntry ? (
        <ConfirmPanel
          title="Are You Sure?"
          description={`Your shift is scheduled to end at ${confirmClockOutEntry.scheduledEnd}. Are you sure you want to clock out early?`}
          confirmLabel="Clock Out"
          tone="rose"
          onCancel={() => setConfirmClockOutId(null)}
          onConfirm={confirmClockOut}
        />
      ) : null}

      {deleteEntry ? (
        <ConfirmPanel
          title="Delete timesheet?"
          description={`Remove ${deleteEntry.staffName}'s timesheet for ${deleteEntry.dayLabel}. Active clocked-in timesheets should be clocked out first.`}
          confirmLabel="Delete"
          tone="rose"
          onCancel={() => setDeleteEntryId(null)}
          onConfirm={deleteTimesheet}
        />
      ) : null}
    </div>
  );
}

function FilterSelect({ icon, value, onChange, children }: { icon: ReactNode; value: string; onChange: (value: string) => void; children: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
      {icon}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="bg-transparent outline-none">
        {children}
      </select>
    </div>
  );
}

function Metric({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-3 py-3">
      <span className={cn('h-9 w-1 rounded-full', color)} />
      <div>
        <p className="text-xs font-semibold text-slate-900">{label}</p>
        <p className="text-sm text-slate-500">{value}</p>
      </div>
    </div>
  );
}

function TimelineDot({ left, active, label, tone = 'emerald' }: { left: string; active: boolean; label: string; tone?: 'emerald' | 'violet' }) {
  return (
    <span
      title={label}
      className={cn(
        'absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 bg-white shadow-sm',
        active ? (tone === 'violet' ? 'border-violet-300' : 'border-emerald-300') : 'border-slate-300',
      )}
      style={{ left }}
    />
  );
}

function IconAction({ label, icon, onClick, disabled }: { label: string; icon: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {icon}
    </button>
  );
}

function SummaryPanel({ icon, label, value, detail }: { icon: ReactNode; label: string; value: string; detail: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-slate-500">
        {icon}
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-2 text-xl font-semibold text-slate-950">{value}</p>
      <p className="mt-1 text-sm text-slate-500">{detail}</p>
    </div>
  );
}

function ActivitySidebar({
  entry,
  onEdit,
  onDelete,
  onApprove,
  onStartBreak,
  onEndBreak,
}: {
  entry: LocalEntry | null;
  onEdit: (entry: LocalEntry) => void;
  onDelete: (entry: LocalEntry) => void;
  onApprove: (entry: LocalEntry) => void;
  onStartBreak: (entry: LocalEntry) => void;
  onEndBreak: (entry: LocalEntry) => void;
}) {
  if (!entry) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="font-semibold text-slate-950">Activity preview</h2>
        <p className="mt-2 text-sm text-slate-500">Select a timesheet row to review clock-ins, breaks, clock-outs, location capture, and admin actions.</p>
      </section>
    );
  }

  const unpaidBreakMinutes = entry.breakMinutes - entry.paidBreakMinutes;
  const paidHours = hoursBetween(entry.clockInAt, entry.clockOutAt, unpaidBreakMinutes);
  const events = [
    entry.clockInAt ? { label: 'Clock-in', detail: `${formatTime(entry.clockInAt)} at ${entry.locationCaptured || entry.location}`, icon: <Play size={14} /> } : null,
    ...entry.breaks.map((item) => ({
      label: item.type,
      detail: `${item.minutes}m ${item.paid ? 'paid' : 'unpaid'}${item.startedAt ? ` from ${formatTime(item.startedAt)}` : ''}`,
      icon: <Coffee size={14} />,
    })),
    entry.clockOutAt ? { label: 'Clock-out', detail: formatTime(entry.clockOutAt), icon: <TimerReset size={14} /> } : null,
  ].filter(Boolean) as Array<{ label: string; detail: string; icon: ReactNode }>;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-slate-950">Activity sidebar</h2>
          <p className="text-sm text-slate-500">{entry.staffName} · {entry.dayLabel}</p>
        </div>
        <span className={cn('rounded-full border px-2 py-1 text-xs font-semibold', statusStyles[entry.status])}>{statusLabel(entry.status)}</span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <SummaryTile label="Hours paid" value={formatDuration(paidHours)} />
        <SummaryTile label="Breaks" value={`${entry.breakMinutes}m`} />
      </div>
      <div className="mt-5 space-y-3">
        {events.map((event, index) => (
          <div key={`${event.label}-${index}`} className="flex gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-600">{event.icon}</span>
            <div>
              <p className="text-sm font-semibold text-slate-900">{event.label}</p>
              <p className="text-xs text-slate-500">{event.detail}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <button type="button" onClick={() => onEdit(entry)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          <Edit3 size={14} /> Edit
        </button>
        {entry.status === 'ON_BREAK' ? (
          <button type="button" onClick={() => onEndBreak(entry)} className="inline-flex items-center gap-2 rounded-lg border border-violet-200 px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50">
            <Play size={14} /> End Break
          </button>
        ) : (
          <button type="button" onClick={() => onStartBreak(entry)} className="inline-flex items-center gap-2 rounded-lg border border-sky-200 px-3 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50">
            <Coffee size={14} /> Add Break
          </button>
        )}
        <button type="button" onClick={() => onApprove(entry)} className="inline-flex items-center gap-2 rounded-lg border border-lime-200 px-3 py-2 text-sm font-semibold text-lime-700 hover:bg-lime-50">
          <Check size={14} /> Approve
        </button>
        <button type="button" onClick={() => onDelete(entry)} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50">
          <Trash2 size={14} /> Delete
        </button>
      </div>
    </section>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-950">{value}</p>
    </div>
  );
}

function TimesheetModal({
  form,
  setForm,
  staffOptions,
  editing,
  creating,
  onSubmit,
  onClose,
}: {
  form: TimesheetForm;
  setForm: React.Dispatch<React.SetStateAction<TimesheetForm>>;
  staffOptions: Array<{ id: string; name: string }>;
  editing: boolean;
  creating: boolean;
  onSubmit: (event: FormEvent) => void;
  onClose: () => void;
}) {
  const clockInAt = combineDateTime(form.date, form.clockIn);
  const clockOutAt = combineDateTime(form.date, form.clockOut);
  const totalWorked = grossHours(clockInAt, clockOutAt);
  const paidBreaks = form.breaks.filter((entry) => entry.paid).reduce((total, entry) => total + Number(entry.minutes || 0), 0);
  const unpaidBreaks = form.breaks.filter((entry) => !entry.paid).reduce((total, entry) => total + Number(entry.minutes || 0), 0);
  const totalPaid = Math.max(0, totalWorked - unpaidBreaks / 60);

  function updateBreak(id: string, partial: Partial<BreakEntry>) {
    setForm((current) => ({ ...current, breaks: current.breaks.map((entry) => (entry.id === id ? { ...entry, ...partial } : entry)) }));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <form onSubmit={onSubmit} className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">{editing ? 'Edit Timesheet' : 'Add Timesheet'}</h2>
            <p className="mt-1 text-sm text-slate-500">Choose employee, location, clock times, and paid or unpaid breaks.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-500 hover:bg-slate-100" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        <div className="grid gap-4 px-6 py-5 md:grid-cols-2">
          <label className="block text-sm">
            <span className="text-slate-500">Team member</span>
            <select value={form.staffId} onChange={(event) => setForm((current) => ({ ...current, staffId: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" required>
              {staffOptions.map((staff) => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-slate-500">Location</span>
            <select value={form.location} onChange={(event) => setForm((current) => ({ ...current, location: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" required>
              {locations.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-slate-500">Date</span>
            <input type="date" value={form.date} onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" required />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-slate-500">Clock in</span>
              <input type="time" value={form.clockIn} onChange={(event) => setForm((current) => ({ ...current, clockIn: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" required />
            </label>
            <label className="block text-sm">
              <span className="text-slate-500">Clock out</span>
              <input type="time" value={form.clockOut} onChange={(event) => setForm((current) => ({ ...current, clockOut: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
            </label>
          </div>
        </div>

        <div className="border-y border-slate-200 bg-slate-50 px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-slate-950">Breaks</h3>
              <p className="text-sm text-slate-500">Use configured blockout options such as lunch, meetings, training, or custom breaks.</p>
            </div>
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              defaultValue=""
              onChange={(event) => {
                const selected = breakTypes.find((item) => item.type === event.target.value);
                if (selected) setForm((current) => ({ ...current, breaks: [...current.breaks, buildBreak(selected.type, selected.minutes, selected.paid)] }));
                event.target.value = '';
              }}
            >
              <option value="" disabled>
                Add Break
              </option>
              {breakTypes.map((item) => (
                <option key={item.type} value={item.type}>
                  {item.type}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4 space-y-2">
            {form.breaks.map((entry) => (
              <div key={entry.id} className="grid grid-cols-[minmax(120px,1fr)_110px_100px_36px] items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
                <select value={entry.type} onChange={(event) => updateBreak(entry.id, { type: event.target.value })} className="rounded-lg border border-slate-200 px-2 py-2 text-sm">
                  {breakTypes.map((item) => (
                    <option key={item.type} value={item.type}>
                      {item.type}
                    </option>
                  ))}
                </select>
                <input type="number" min="0" value={entry.minutes} onChange={(event) => updateBreak(entry.id, { minutes: Number(event.target.value) })} className="rounded-lg border border-slate-200 px-2 py-2 text-sm" aria-label="Break minutes" />
                <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={entry.paid} onChange={(event) => updateBreak(entry.id, { paid: event.target.checked })} />
                  Paid
                </label>
                <button type="button" onClick={() => setForm((current) => ({ ...current, breaks: current.breaks.filter((item) => item.id !== entry.id) }))} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Remove break">
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 px-6 py-5 md:grid-cols-[minmax(0,1fr)_260px]">
          <label className="block text-sm">
            <span className="text-slate-500">Notes</span>
            <textarea value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} rows={5} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" placeholder="Late clock-in, correction reason, or manager note" />
          </label>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="font-semibold text-slate-950">Total Hours</h3>
            <div className="mt-3 space-y-3 text-sm">
              <SummaryLine label="Total hours worked" value={formatDuration(totalWorked)} />
              <SummaryLine label="Paid breaks" value={formatDuration(paidBreaks / 60)} />
              <SummaryLine label="Unpaid breaks" value={formatDuration(unpaidBreaks / 60)} />
              <SummaryLine label="Total hours paid" value={formatDuration(totalPaid)} strong />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={creating} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60">
            {creating ? 'Saving...' : editing ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

function SummaryLine({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex items-center justify-between gap-3', strong && 'border-t border-slate-200 pt-3 font-semibold text-slate-950')}>
      <span className="text-slate-500">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function ToggleRow({ checked, onChange, title, description }: { checked: boolean; onChange: (checked: boolean) => void; title: string; description: string }) {
  return (
    <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="mt-1" />
      <span>
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="block text-sm text-slate-500">{description}</span>
      </span>
    </label>
  );
}

function ConfirmPanel({
  title,
  description,
  children,
  confirmLabel,
  tone,
  onCancel,
  onConfirm,
}: {
  title: string;
  description: string;
  children?: ReactNode;
  confirmLabel: string;
  tone: 'rose' | 'sky';
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">{title}</h2>
            <p className="mt-2 text-sm text-slate-600">{description}</p>
          </div>
          <button type="button" onClick={onCancel} className="rounded-lg p-1 text-slate-500 hover:bg-slate-100" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        {children ? <div className="mt-5">{children}</div> : null}
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className={cn('rounded-lg px-4 py-2 text-sm font-semibold text-white', tone === 'rose' ? 'bg-rose-500 hover:bg-rose-600' : 'bg-sky-600 hover:bg-sky-700')}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
